import type { LatLng, Place } from "./types";
import { namedPlace } from "./placeLabel";

const MAX_PLACES = 5000;
type StoredPlace = Place & { savedAt: number };
let opening: Promise<IDBDatabase | null> | undefined;
function database(): Promise<IDBDatabase | null> {
  return opening ??= new Promise(resolve => {
    try {
      const request = indexedDB.open("desty-offline-places-v1", 1);
      request.onupgradeneeded = () => request.result.createObjectStore("places", {keyPath:"id"});
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch { resolve(null); }
  });
}
function rows(db: IDBDatabase): Promise<StoredPlace[]> {
  return new Promise(resolve => {
    try {
      const request=db.transaction("places").objectStore("places").getAll();
      request.onsuccess=()=>resolve(request.result);
      request.onerror=()=>resolve([]);
    } catch { resolve([]); }
  });
}
let writing=Promise.resolve();
/** Only public local-map records; never Google results, account data or routes. */
export function rememberOfflinePlaces(places: Place[]): Promise<void> {
  writing=writing.then(async()=>{
    const db=await database(); if(!db)return;
    const safe=places.filter(p=>["osm","overture","community"].includes(p.source??"") && namedPlace(p)
      && Number.isFinite(p.lat) && Number.isFinite(p.lng) && Math.abs(p.lat)<=90 && Math.abs(p.lng)<=180);
    if(!safe.length)return;
    const existing=await rows(db);
    const merged=new Map(existing.map(p=>[p.id,p]));
    safe.slice(0,1000).forEach(p=>merged.set(p.id,{...p,savedAt:Date.now()}));
    const kept=[...merged.values()].sort((a,b)=>b.savedAt-a.savedAt).slice(0,MAX_PLACES);
    await new Promise<void>(resolve=>{
      const transaction=db.transaction("places","readwrite");const store=transaction.objectStore("places");
      store.clear();kept.forEach(p=>store.put(p));
      transaction.oncomplete=()=>resolve();transaction.onerror=()=>resolve();transaction.onabort=()=>resolve();
    });
  }).catch(()=>{});
  return writing;
}
const normalized=(s:string)=>s.normalize("NFKD").replace(/[\u0591-\u05C7]/g,"").toLocaleLowerCase()
  .replace(/[ךםןףץ]/g,c=>({ך:"כ",ם:"מ",ן:"נ",ף:"פ",ץ:"צ"})[c]!).replace(/[^\p{L}\p{N}]+/gu," ").trim();
export function searchDownloadedPlaces(places: Place[],query:string,point:LatLng): Place[] {
  const tokens=normalized(query).split(" ").filter(Boolean); if(!tokens.length)return [];
  return places.filter(p=>{
    const text=normalized(p.name+" "+p.address);
    return tokens.every(t=>text.split(" ").some(word=>word.startsWith(t)));
  }).sort((a,b)=>((a.lat-point.lat)**2+(a.lng-point.lng)**2*Math.cos(point.lat*Math.PI/180)**2)
    -((b.lat-point.lat)**2+(b.lng-point.lng)**2*Math.cos(point.lat*Math.PI/180)**2)).slice(0,12);
}
export async function offlineSearch(query:string,point:LatLng):Promise<Place[]> {
  const db=await database();return db?searchDownloadedPlaces(await rows(db),query,point):[];
}
export function downloadedPlaceNear(places:Place[],point:LatLng):Place|null {
  const scale=Math.cos(point.lat*Math.PI/180);
  const distance=(p:Place)=>Math.hypot(p.lat-point.lat,(p.lng-point.lng)*scale)*111_195;
  const nearest=places.filter(p=>namedPlace(p) && distance(p)<=30).sort((a,b)=>distance(a)-distance(b))[0];
  return nearest?{...nearest,lat:point.lat,lng:point.lng}:null;
}
export async function offlineReverse(point:LatLng):Promise<Place|null> {
  const db=await database();return db?downloadedPlaceNear(await rows(db),point):null;
}
